import PropTypes from 'prop-types';
import { Text, Stack } from '@/components/ui/index.js';
import styles from './ToggleItem.module.css';

/**
 * ToggleItem - Settings row with animated toggle switch
 * Used for boolean settings like notifications
 */
export function ToggleItem({
  icon: Icon,
  label,
  description,
  checked,
  onChange,
  disabled = false,
  className = '',
  ...props
}) {
  const classNames = [
    styles.item,
    disabled && styles.disabled,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      className={classNames}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="switch"
      aria-checked={checked}
      tabIndex={disabled ? -1 : 0}
      {...props}
    >
      <Stack direction="row" gap={3} align="center" className={styles.left}>
        {Icon && (
          <div className={styles.iconWrapper}>
            <Icon size={20} strokeWidth={2} />
          </div>
        )}
        <Stack direction="column" gap={0}>
          <Text size="base" color="primary" className={styles.label}>
            {label}
          </Text>
          {description && (
            <Text size="sm" color="muted" className={styles.description}>
              {description}
            </Text>
          )}
        </Stack>
      </Stack>
      <div className={styles.right}>
        <div
          className={`${styles.toggle} ${checked ? styles.checked : ''}`}
          aria-hidden="true"
        >
          <div className={styles.toggleThumb} />
        </div>
      </div>
    </div>
  );
}

ToggleItem.propTypes = {
  icon: PropTypes.elementType,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default ToggleItem;
